import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import { salaryFrequency } from '../../constants/salary';
import analytics from '../../lib/analytics';
import applyDefaultCurrencyOn from '../../lib/defaultCurrencyHelper';
import { isNumber, isNumberNegative } from '../../lib/helpers';
import useDefaultCurrency from '../../lib/hooks/useDefaultCurrency';
import { currencyFormattedNumber } from '../../lib/numberFormatter';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { currentSalarySelector } from '../../reducers/selectors';
import { updateValue } from '../../utils/utils';

const FutureSalaryPage = ({
  initialFutureSalary,
  currentSalary,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const [defaultCurrency, currencies] = useDefaultCurrency();
  const {
    type: initialSalaryType,
    currency: initialSalaryCurrency,
    amount: initialSalaryAmount,
  } = applyDefaultCurrencyOn(initialFutureSalary, defaultCurrency);
  const { amount: currentSalaryAmount, currency: currentSalaryCurrency } = currentSalary;

  const [salaryType, setSalaryType] = useState(initialSalaryType);
  const [salaryCurrency, setSalaryCurrency] = useState(initialSalaryCurrency);
  const [salaryAmount, setSalaryAmount] = useState(initialSalaryAmount);

  const [debouncedSalaryAmount, setDebouncedSalaryAmount] = useState(initialSalaryAmount);

  useEffect(() => {
    // Set a timeout to update the debounced salary amount after 500ms
    const timerId = setTimeout(() => {
      setDebouncedSalaryAmount(salaryAmount);
    }, 500);

    // Clear the timeout if the salary amount changes before the 500ms are up
    return () => {
      clearTimeout(timerId);
    };
  }, [salaryAmount]);

  const isInvalid = () => {
    return (
      salaryType.trim().length === 0 ||
      salaryCurrency.trim().length === 0 ||
      salaryAmount.trim().length === 0 ||
      !salaryFrequency[salaryType] ||
      !isNumber(salaryAmount) ||
      isNumberNegative(salaryAmount) ||
      Number.parseFloat(salaryAmount) === 0
    );
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid()) {
      return;
    }

    analytics.sendEvent('Click - Next', 'future-salary');

    const payload = payloadFactory.createAddPageDataPayload('futureSalary', {
      type: salaryType,
      currency: salaryCurrency,
      amount: salaryAmount,
    });
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const changeCurrency = event => {
    const currency = event.target.value;
    setSalaryCurrency(currency);
  };

  const changeSalaryType = event => {
    const type = event.target.value;
    setSalaryType(type);
  };

  const renderAnnualSalaryHelper = () => {
    if (isInvalid()) {
      return null;
    }

    const inputAmount = Number.parseFloat(salaryAmount);
    const annualAmount = salaryFrequency[salaryType].conversion(inputAmount);
    const formattedAnnualAmount = currencyFormattedNumber(salaryCurrency, annualAmount);

    return (
      <div className="row-spacer">
        That means you earn&nbsp;
        <b>{formattedAnnualAmount} a year.</b>
      </div>
    );
  };

  const renderSalaryAmountWarning = () => {
    if (isInvalid()) {
      return null;
    }

    const currentInputAmount = Number.parseFloat(currentSalaryAmount) || 0;
    const currentAnnualAmount = salaryFrequency['MONTHLY'].conversion(currentInputAmount);
    const formattedCurrentAnnualAmount = currencyFormattedNumber(
      currentSalaryCurrency,
      currentAnnualAmount,
    );

    const futureInputAmount = Number.parseFloat(debouncedSalaryAmount);
    const futureAnnualAmount = salaryFrequency[salaryType].conversion(futureInputAmount);

    if (currentAnnualAmount > futureAnnualAmount && currentSalaryCurrency === salaryCurrency) {
      return (
        <div className="row-spacer text-error">
          The salary you entered is less than what you're currently earning (
          {formattedCurrentAnnualAmount} annually). If this is correct, you can disregard this
          message.
        </div>
      );
    } else {
      return null;
    }
  };

  const getSalaryTypeOptions = () => {
    const salaryFrequencyArray = Object.entries(salaryFrequency);
    const options = salaryFrequencyArray.map(([value, { label }]) => {
      return { value, label };
    });
    options.unshift({ value: '', label: 'Select salary type' });

    return options;
  };

  const helpText =
    'Tell us your gross salary in the currency listed on the job offer, before taxes and excluding any bonuses';

  return (
    <PageLayout
      title="What’s the gross salary of the job offer that you have accepted?"
      helpText={helpText}
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--11/16-small">
            <Select
              id="salary-type"
              value={salaryType}
              onChange={changeSalaryType}
              options={getSalaryTypeOptions()}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col--11/16-small">
            <CombinedTextField>
              <Select
                id="salary-currency"
                value={salaryCurrency}
                onChange={changeCurrency}
                options={currencies}
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="future-salary"
                placeholder="Enter amount"
                value={salaryAmount}
                onChange={e => updateValue(e, setSalaryAmount, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>
        </div>

        {renderAnnualSalaryHelper()}
        {renderSalaryAmountWarning()}

        <div className="row-spacer">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

FutureSalaryPage.propTypes = {
  initialFutureSalary: PropTypes.shape({
    type: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  }).isRequired,
  currentSalary: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  initialFutureSalary: state.builder.ui.futureSalary,
  currentSalary: currentSalarySelector(state),
});

const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { FutureSalaryPage, mapStateToProps, mapDispatchToProps };

export default connect(mapStateToProps, mapDispatchToProps)(FutureSalaryPage);
